<script lang="ts" setup>
import {computed, ref, useTemplateRef} from "vue";
import {useCookies} from "@vueuse/integrations/useCookies";
import FileUpload, {
    FileUploadBeforeUploadEvent,
    FileUploadErrorEvent,
    FileUploadRemoveEvent,
    FileUploadSelectEvent,
    FileUploadUploadEvent,
} from "primevue/fileupload";
import {Icon} from "@iconify/vue";

type FileUploadType = typeof FileUpload & {
    upload: () => void;
    clear: () => void;
    files: Array<File>;
};
const files = ref<Array<File>>([]);
const uploader = useTemplateRef<FileUploadType | null>("uploader");
const xcsrfToken = useCookies(["XSRF-TOKEN"]).get("XSRF-TOKEN");

const props = withDefaults(
    defineProps<{
        collection: string;
        parentType: string;
        parentId?: string;
        multiple?: boolean;
        accept?: string;
        maxFileSize?: number;
        fileLimit?: number;
        placeholder?: string;
        disableUploadButton?: boolean;
        showUploadButton?: boolean;
        disabled?: boolean;
        disabledMessage?: string;
        mode?: string;
        pt?: any;
        id?: string;
    }>(),
    {
        multiple: false,
        maxFileSize: 5 * 100 * 1000 * 1000, // 500MB
        // placeholder: "Drag and drop files to here to upload (up to 500MB per file).",
        disableUploadButton: false,
        showUploadButton: false,
        disabled: false,
        pt: {},
        mode: "advanced",
    },
);

const placeholder = computed(
    () =>
        props.placeholder ||
        `Drag and drop ${props.fileLimit === 1 ? "a file" : "files"} here to upload (up to ${props.maxFileSize / 1000000}MB${props.fileLimit && props.fileLimit > 1 ? " per file" : ""}).`,
);

const emit = defineEmits(["upload", "select", "error", "remove", "clear"]);

const url = computed(() =>
    route("media.store", {
        modelType: props.parentType,
        modelId: props.parentId ?? 0,
        collectionName: props.collection,
    }),
);

const handleBeforeSend = (event: FileUploadBeforeUploadEvent) => {
    event.xhr.setRequestHeader("X-XSRF-TOKEN", xcsrfToken);
};

const handleBeforeUpload = (event: FileUploadBeforeUploadEvent) => {
    // console.log("token", _token.get("XSRF-TOKEN"));
    // event.formData.append("_token", _token);
};

const handleUpload = (event: FileUploadUploadEvent) => {
    savedResolve.value?.(event);
    emit("upload", event);
};
const handleSelect = (event: FileUploadSelectEvent) => {
    files.value = event.files;
    emit("select", event);
};
const handleError = (event: FileUploadErrorEvent) => {
    savedReject.value?.(event);
    emit("error", event);
};

const handleRemove = (event: FileUploadRemoveEvent) => emit("remove", event);

const handleClear = () => emit("clear");
const uploadPromise = ref<Promise<any> | null>(null);
const savedResolve = ref<Function | null>(null);
const savedReject = ref<Function | null>(null);
const startUpload = () => {
    if (!uploader.value?.files.length) {
        return Promise.resolve([]);
    }
    uploadPromise.value = new Promise(async (resolve, reject) => {
        savedResolve.value = resolve;
        savedReject.value = reject;

        if (uploader.value?.files.length) {
            uploader.value?.upload();
        }
    });

    return uploadPromise.value;
    // return new Promise(async (resolve, reject) => {
    //     resolve(
    //         uploader.value?.files.map((file) => ({
    //             name: file.name,
    //             size: file.size,
    //             type: file.type,
    //             url: URL.createObjectURL(file),
    //         })),
    //     );
    // });
};

const clearFiles = () => {
    console.log(uploader.value);
    uploader.value?.clear();
    uploader.value.files = [];
};

defineExpose({
    id: props.id,
    startUpload,
    clearFiles,
    files,
    model: {
        id: props.parentId,
        type: props.parentType,
    },
    url: url.value,
});
</script>
<template>
    <div class="relative">
        <FileUpload
            :pt
            ref="uploader"
            :id
            :accept
            :fileLimit
            :maxFileSize
            :multiple
            name="files[]"
            :url
            :withCredentials="true"
            :show-upload-button="showUploadButton"
            :mode
            @error="handleError"
            @select="handleSelect"
            @upload="handleUpload"
            @remove="handleRemove"
            @beforeSend="handleBeforeSend"
            @beforeUpload="handleBeforeUpload"
            @clear="handleClear"
            :disabled
            :uploadButtonProps="{disabled: disabled || !props.parentId}"
            :pt:cointent:class="'foo'"
        >
            <template #empty>
                <div
                    v-if="disabled"
                    class="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-lg text-xs"
                >
                    <div v-if="disabledMessage" class="p-6">{{ disabledMessage }}</div>
                    <div v-else class="mt-6 p-6">This field is disabled</div>
                </div>
                <div v-else class="hidden flex-col items-center justify-center lg:flex">
                    <Icon icon="prime:cloud-upload" width="60" />
                    <p class="mb-0 mt-4">{{ placeholder }}</p>
                </div>
            </template>
        </FileUpload>
    </div>
</template>
<style scoped>
.p-fileupload-highlight {
    @apply bg-askr-green-50;
}
</style>
